import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { convertToDeal } from "../lib/convertToDeal";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertTriangle, ArrowLeft, ExternalLink, Loader2, RefreshCw, PencilLine } from "lucide-react";
import { toast } from "sonner";

const TIER_STYLE = {
    High: "bg-green-100 text-green-800 border-green-300",
    Medium: "bg-amber-100 text-amber-800 border-amber-300",
    Low: "bg-red-100 text-red-700 border-red-300",
};

const gbp = (n) => (n === null || n === undefined ? "—" : `£${Math.round(n).toLocaleString("en-GB")}`);
const gbpShort = (n) => {
    if (n === null || n === undefined) return "—";
    const a = Math.abs(n);
    if (a >= 1e6) return `£${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2)}m`;
    if (a >= 1e3) return `£${Math.round(n / 1e3)}k`;
    return `£${Math.round(n)}`;
};
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-GB"); } catch { return d; } };

const ROWS = [
    ["Fixed assets", "fixed_assets_total"],
    ["Stocks", "current_assets_stocks"],
    ["Debtors", "current_assets_debtors"],
    ["Cash", "current_assets_cash"],
    ["Current assets", "current_assets_total"],
    ["Creditors < 1 year", "creditors_within_one_year"],
    ["Net current assets", "net_current_assets"],
    ["Creditors > 1 year", "creditors_after_one_year"],
    ["Net assets", "net_assets"],
    ["Profit & loss reserve", "capital_profit_loss_account"],
    ["Employees", "employees"],
];

const SOURCE_LABEL = { ch_ixbrl: "Companies House (iXBRL)", ch_xbrl: "Companies House (XBRL)", manual_paper_filing: "Manual (paper filing)" };

const FORM_FIELDS = [
    ["fixed_assets_total", "Fixed assets (total)"],
    ["current_assets_stocks", "Stocks"],
    ["current_assets_debtors", "Debtors"],
    ["current_assets_cash", "Cash at bank and in hand"],
    ["current_assets_total", "Current assets (total)"],
    ["creditors_within_one_year", "Creditors: due within one year"],
    ["net_current_assets", "Net current assets"],
    ["creditors_after_one_year", "Creditors: due after one year"],
    ["net_assets", "Net assets"],
    ["capital_profit_loss_account", "Profit and loss account (reserves)"],
    ["employees", "Average employees"],
];

function Range({ label, r, sub }) {
    return (
        <div className="flex-1 min-w-[260px]">
            <p className="text-[11px] uppercase tracking-wider text-[#94A3B8]">{label}</p>
            <p className="text-[26px] font-semibold text-[#0F2A47] leading-tight" data-testid={`mid-${label}`}>{gbpShort(r?.mid)}</p>
            <p className="text-[12px] text-[#64748B]">Range {gbpShort(r?.low)} – {gbpShort(r?.high)}</p>
            {sub && <p className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</p>}
            {r && r.tier && (
                <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline" className={TIER_STYLE[r.tier]}>{r.tier} confidence</Badge>
                    <span className="text-[12px] text-[#64748B]">{r.confidence}/100</span>
                </div>
            )}
        </div>
    );
}

function ManualDialog({ number, filingUrl, open, onOpenChange, onSaved }) {
    const empty = { period_end_date: "", filing_date: "" };
    const [form, setForm] = useState(empty);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            const body = { period_end_date: form.period_end_date, filing_date: form.filing_date, confirmed, source: "manual_paper_filing" };
            FORM_FIELDS.forEach(([k]) => {
                const raw = String(form[k] ?? "").replace(/[£,\s]/g, "");
                if (raw !== "") body[k] = Math.round(Number(raw));
            });
            await api.post(`/sourcing/companies/${number}/financials/manual`, body);
            toast.success("Year saved. Enter another year, or close and recalculate.");
            setForm(empty);
            setConfirmed(false);
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
        } finally { setBusy(false); }
    };
    const ready = confirmed && /^\d{4}-\d{2}-\d{2}$/.test(form.period_end_date) && /^\d{4}-\d{2}-\d{2}$/.test(form.filing_date);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Enter figures from a filed document</DialogTitle></DialogHeader>
                <p className="text-[13px] text-[#475569]">
                    Enter figures exactly as shown in the filed document. Do not estimate — estimated figures should use the benchmark calculation instead.
                    {filingUrl && <> <a href={filingUrl} target="_blank" rel="noreferrer" className="text-[#0F2A47] underline">Open filing history</a>.</>}
                    {" "}Add one year at a time; repeat for earlier years to improve confidence.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-[12px] text-[#475569]">Period end date (YYYY-MM-DD)
                        <Input value={form.period_end_date} onChange={e => set("period_end_date", e.target.value)} placeholder="2024-03-31" data-testid="manual-period-end" />
                    </label>
                    <label className="text-[12px] text-[#475569]">Filing date (YYYY-MM-DD)
                        <Input value={form.filing_date} onChange={e => set("filing_date", e.target.value)} placeholder="2024-11-20" data-testid="manual-filing-date" />
                    </label>
                    {FORM_FIELDS.map(([k, label]) => (
                        <label key={k} className="text-[12px] text-[#475569]">{label}
                            <Input inputMode="numeric" value={form[k] ?? ""} onChange={e => set(k, e.target.value)} placeholder="£" data-testid={`manual-${k}`} />
                        </label>
                    ))}
                </div>
                <label className="flex items-start gap-2 text-[13px] text-[#0F2A47] mt-2">
                    <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1" data-testid="manual-confirm" />
                    I confirm these figures are transcribed from the filed document referenced above
                </label>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                    <Button disabled={!ready || busy} onClick={save} data-testid="manual-save">
                        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}Save year
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function TargetAnalysis() {
    const { number } = useParams();
    const nav = useNavigate();
    const { hasSurface } = useAuth();
    const [converting, setConverting] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/sourcing/companies/${number}/analysis`);
            setData(data);
            setError(null);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load analysis");
        }
    }, [number]);

    useEffect(() => { load(); }, [load]);

    const recalc = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post(`/sourcing/companies/${number}/analysis/recalculate`);
            setData(d => ({ ...d, analysis: r.analysis, stale: false, stale_reason: null }));
            toast.success("Analysis recalculated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Recalculation failed");
        } finally { setBusy(false); }
    };

    const convert = async () => {
        setConverting(true);
        try {
            await api.post("/sourcing/candidates", { company_number: number }); // idempotent: ensures it is in the pipeline
            await convertToDeal(`/sourcing/candidates/${number}/convert-to-deal`, nav);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not convert");
        } finally { setConverting(false); }
    };

    if (error) return <div className="p-6 w-full"><p className="text-red-600 text-[14px]">{error}</p></div>;
    if (!data) return <div className="p-6 w-full flex items-center gap-2 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

    const a = data.analysis;
    const t = a?.turnover;
    const v = a?.valuation;
    const psc = data.psc_summary;
    const years = [...(data.financials || [])].sort((x, y) => (y.period_end_date || "").localeCompare(x.period_end_date || ""));
    const filingUrl = data.accounts_status?.filing_history_url;
    const noStructured = data.accounts_status?.state === "no_structured_accounts";
    const explanation = Array.isArray(t?.explanation) ? t.explanation : t?.explanation ? [t.explanation] : [];

    return (
        <div className="w-full" data-testid="target-analysis">
            <PageHeader title={data.company_name || number} subtitle={`Company ${number} — target analysis`}>
                <Link to="/sourcing/mandates"><Button variant="outline" size="sm"><ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Sourcing</Button></Link>
                {hasSurface("deal_management") && (
                    <Button size="sm" onClick={convert} disabled={converting} data-testid="convert-to-deal">
                        {converting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}Convert to deal
                    </Button>
                )}
                <a href={`https://find-and-update.company-information.service.gov.uk/company/${number}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Companies House</Button>
                </a>
            </PageHeader>

            <div className="px-6 pb-8 space-y-5 w-full">
                {data.stale && (
                    <div className="flex items-center justify-between gap-3 border border-amber-300 bg-amber-50 text-amber-900 rounded px-4 py-3 text-[13px]" data-testid="stale-banner">
                        <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{data.stale_reason}</span>
                        <Button size="sm" onClick={recalc} disabled={busy}>Recalculate</Button>
                    </div>
                )}

                {noStructured && (
                    <div className="border border-[#E2E8F0] bg-[#F8FAFC] rounded px-4 py-3 text-[13px] text-[#475569]" data-testid="accounts-status">
                        <p>{data.accounts_status.message}</p>
                        <div className="mt-2 flex gap-2">
                            <a href={filingUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />View filing history</Button></a>
                            <Button variant="outline" size="sm" onClick={() => setManualOpen(true)} data-testid="open-manual"><PencilLine className="w-3.5 h-3.5 mr-1.5" />Enter figures from filing</Button>
                        </div>
                    </div>
                )}

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-[15px] flex items-center justify-between">
                            <span>Estimated turnover and valuation</span>
                            <span className="flex gap-2">
                                {!noStructured && <Button variant="outline" size="sm" onClick={() => setManualOpen(true)}><PencilLine className="w-3.5 h-3.5 mr-1.5" />Add year manually</Button>}
                                <Button size="sm" onClick={recalc} disabled={busy || !data.calculable} data-testid="recalculate">
                                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                                    {a ? "Recalculate" : "Run analysis"}
                                </Button>
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!a && <p className="text-[13px] text-[#64748B]">
                            {data.calculable ? "No analysis has been run for this company yet." : "No financial data is held for this company yet, so there is nothing to calculate from."}
                        </p>}
                        {a && (
                            <div className="space-y-4">
                                <div className="flex flex-wrap gap-8">
                                    <Range label="Turnover" r={t} sub={t?.years_used ? `Based on ${t.years_used} year${t.years_used > 1 ? "s" : ""} of accounts` : null} />
                                    <Range label="Enterprise value" r={v} sub="Before net debt" />
                                </div>
                                <p className="text-[11px] text-[#94A3B8]">Calculated {fmtDate(a.calculated_at)}. Estimates are retained until you recalculate.</p>
                                <div className="grid md:grid-cols-3 gap-4 text-[12px]">
                                    <div><p className="font-semibold text-[#0F2A47] mb-1">How turnover was estimated</p>
                                        <ul className="list-disc ml-4 space-y-0.5 text-[#475569]">
                                            {explanation.map((e, i) => <li key={i}>{typeof e === "string" ? e : JSON.stringify(e)}</li>)}
                                            {explanation.length === 0 && (t?.methods || []).map((m, i) => <li key={i}>{m.name || m.method}: {gbpShort(m.estimate ?? m.value)}</li>)}
                                        </ul></div>
                                    <div><p className="font-semibold text-[#0F2A47] mb-1">Valuation assumptions</p>
                                        <ul className="list-disc ml-4 space-y-0.5 text-[#475569]">{(v?.assumptions || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                                    <div><p className="font-semibold text-[#0F2A47] mb-1">Why this confidence level</p>
                                        <ul className="list-disc ml-4 space-y-0.5 text-[#475569]">
                                            {[...(t?.reasons || []), ...(v?.reasons || [])].filter((x, i, arr) => arr.indexOf(x) === i).map((x, i) => <li key={i}>{x}</li>)}
                                        </ul></div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-[15px]">Owners (PSC)</CardTitle></CardHeader>
                    <CardContent className="text-[13px] text-[#475569]">
                        {!psc ? "No PSC data held for this company yet." : (
                            <div className="flex flex-wrap gap-x-8 gap-y-1">
                                <span>Active individual PSCs: <b>{psc.active_individual_pscs ?? 0}</b></span>
                                <span>Oldest owner age: <b>{psc.oldest_age ?? "—"}</b>{psc.age_band ? ` (${psc.age_band})` : ""}</span>
                                <span>{psc.sole_individual ? "Sole individual owner" : "Multiple / corporate owners"}</span>
                                {psc.succession_flag && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Succession signal</Badge>}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-[15px]">Financial profile</CardTitle></CardHeader>
                    <CardContent className="overflow-x-auto">
                        {years.length === 0 ? <p className="text-[13px] text-[#64748B]">No accounts held.</p> : (
                            <table className="w-full text-[12px]" data-testid="financials-table">
                                <thead>
                                    <tr className="text-[11px] text-[#94A3B8] uppercase tracking-wider">
                                        <th className="text-left py-1.5 pr-4">Year end</th>
                                        {years.map(y => <th key={y.period_end_date} className="text-right px-3">{fmtDate(y.period_end_date)}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ROWS.map(([label, key]) => (
                                        <tr key={key} className="border-t border-[#F1F5F9]">
                                            <td className="py-1.5 pr-4 text-[#475569]">{label}</td>
                                            {years.map(y => <td key={y.period_end_date} className="text-right px-3 tabular-nums">{y[key] === undefined || y[key] === null ? "—" : key === "employees" ? y[key] : gbp(y[key])}</td>)}
                                        </tr>
                                    ))}
                                    <tr className="border-t border-[#E2E8F0]">
                                        <td className="py-1.5 pr-4 text-[#94A3B8]">Source</td>
                                        {years.map(y => <td key={y.period_end_date} className="text-right px-3 text-[#94A3B8]">{SOURCE_LABEL[y.source] || y.source || "—"}</td>)}
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <ManualDialog number={number} filingUrl={filingUrl} open={manualOpen} onOpenChange={setManualOpen} onSaved={load} />
        </div>
    );
}
